import React, { useState } from 'react'
import Icon from 'components/elements/Icon'
import { useCookies } from 'react-cookie'

import { useMobileControlToggles } from 'hooks/useMobileControlToggles.module'
import { useActionless, useAppSelector } from 'app-redux/hooks'

import { Option } from 'components/elements/ArchidektDropdown/types'

import { store } from 'app-redux/store'
import { SET_CACHEABLES_STATE } from 'app-redux/cacheables/actions/types'

import { SET_ACTIVE_STATE, TOGGLE_MODAL } from 'app-redux/active/actions/types'
import { StickiableControls } from 'app-redux/cacheables/initialState'

import Link from 'components/elements/Link'
import ContextMenu from 'components/elements/ContextMenu'
import SaveWatcher from 'components/deckPage/DeckContainerV2/SaveWatcher'
import saveService from 'services/save.service'
import CustomIcon from 'components/elements/CustomIcon'

import { useRightPanel } from 'hooks/usePinnedSidecar'
import { useCanEditDeck } from 'app-redux/deck/selectors'

import styles from './stickiedDeckControls.module.scss'

type Props = { className?: string; liteControls?: boolean }

const StickiedDeckControls = ({ className = '', liteControls }: Props) => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const [mobileControls] = useMobileControlToggles()

  const canEditDeck = useCanEditDeck()

  const deckId = useAppSelector(state => state.deck.id)
  const undoStack = useAppSelector(state => state.deck.actionStack)
  const redoStack = useAppSelector(state => state.deck.undoneActionStack)
  const staticCategoy = useAppSelector(state => state.cacheables.pinnedCategory)
  const ownerId = useAppSelector(state => state.deck.ownerid)
  const snapshotMeta = useAppSelector(state => state.deck.snapshotMeta)

  const viewableDeckControls = useAppSelector(state => state.cacheables.viewableDeckControls)

  const [toggleModal, setCacheableState, setActiveState] = useActionless(
    TOGGLE_MODAL,
    SET_CACHEABLES_STATE,
    SET_ACTIVE_STATE,
  )

  const [rightPanelStatus, setRightPanelStatus] = useRightPanel()

  const [localViewableContorls, setLocalViewableContorls] = useState(viewableDeckControls)

  const handleToggleViewableDeckControls = (control: StickiableControls) =>
    setLocalViewableContorls({ ...localViewableContorls, [control]: !localViewableContorls[control] })

  const handleApplyControlChanges = () => {
    setCacheableState({ viewableDeckControls: localViewableContorls })
  }

  const handleSetAllLocalViewableToggles = (flag: boolean) => {
    const newToggles = Object.keys(localViewableContorls).reduce(
      (acc, key) => ({ ...acc, [key]: flag }),
      {} as typeof localViewableContorls,
    )

    setLocalViewableContorls(newToggles)
  }

  const options: (Option & { id?: StickiableControls })[] = [
    {
      label: 'Card search',
      id: 'cardSearch',
      icon: 'search',
      onClick: () => setActiveState({ searchOpen: 'search' }),
      keybind: 'Ctrl + Enter',
    },
    { label: 'EDH Recs', customIcon: 'edhrec', onClick: () => setActiveState({ searchOpen: 'recs' }), id: 'edhrec' },
    {
      label: 'Card packages',
      id: 'packages',
      icon: 'cube',
      onClick: () => toggleModal({ modalName: 'cardPackageModal', flag: true }),
    },
    { type: 'spacer' },
    {
      label: 'Import cards',
      id: 'import',
      icon: 'edit outline',
      onClick: () => toggleModal({ modalName: 'massEditModal', flag: true }),
      hidden: !canEditDeck,
    },
    {
      label: 'Export deck',
      id: 'export',
      onClick: () => toggleModal({ modalName: 'export', flag: true }),
      icon: 'download',
    },
    {
      label: 'Buy / sell cards',
      onClick: () => toggleModal({ modalName: 'bulkBuy', flag: true }),
      icon: 'shopping cart',
      id: 'buySell',
    },
    { type: 'spacer' },
    {
      label: 'Clone deck',
      id: 'clone',
      onClick: () => toggleModal({ modalName: 'cloneModal', flag: true }),
      icon: 'clone',
      hidden: !userId,
    },
    {
      label: 'Share deck',
      icon: 'share alternate',
      id: 'share',
      onClick: () => toggleModal({ modalName: 'shareDeck', flag: true }),
      hidden: !deckId,
    },
    {
      label: 'Compare deck',
      icon: 'exchange',
      id: 'compare',
      to: `/partialCompare?one=${!!snapshotMeta ? 's_' : 'd_'}${deckId}&diffFields=totalQuantity`,
      hidden: !deckId,
    },
    { type: 'spacer' },
    {
      label: 'Add deck to collection',
      id: 'addToCollection',
      onClick: () => toggleModal({ modalName: 'addToCollection', flag: true }),
      icon: 'circle-plus',
      hidden: !userId,
    },
    { type: 'spacer', hidden: !userId },
    {
      label: 'Select all',
      id: 'selectAll',
      keybind: 'Ctrl + A',
      onClick: () => {
        const { cardMap } = store.getState().deck
        const multiSelectedIds = Object.keys(cardMap).reduce((a, id) => ({ ...a, [id]: true }), {})

        setActiveState({ multiSelectedIds })
      },
      icon: 'hand pointer outline',
    },
    {
      label: 'Optimize printings',
      id: 'optimize',
      onClick: () => {
        const { cardMap } = store.getState().deck
        const multiSelectedIds = Object.keys(cardMap).reduce((a, id) => ({ ...a, [id]: true }), {})

        setActiveState({ multiSelectedIds, optimizeCardOverlay: true })
      },
      icon: 'money bill alternate outline',
    },
    {
      label: 'Optimize basics',
      id: 'lands',
      icon: 'fire',
      onClick: () => setActiveState({ autofillBasicLandsOverlay: true }),
    },
    {
      label: 'Add custom cards',
      id: 'customCards',
      icon: 'file image outline',
      hidden: `${userId}` !== `${ownerId}` || !!snapshotMeta,
      onClick: () => setActiveState({ selectCustomCards: true }),
    },
    { type: 'spacer' },
    {
      label: 'Deck stats',
      id: 'stats',
      icon: 'pie graph',
      disabled: rightPanelStatus.locked,
      keybind: 'Ctrl + M',
      onClick: () => setRightPanelStatus({ ...rightPanelStatus, open: true }),
    },
    {
      label: 'Deck history',
      id: 'history',
      onClick: () => toggleModal({ modalName: 'addRemovedCards', flag: true }),
      icon: 'history',
    },
    {
      label: 'Deck settings',
      id: 'settings',
      onClick: () => toggleModal({ modalName: 'deckSettings', flag: true }),
      icon: 'cog',
      hidden: !canEditDeck,
    },
  ]

  return (
    <div
      className={`${styles.controls} ${staticCategoy ? styles.pinnedCategoryEnabled : ''} ${mobileControls.stickyBar ? styles.noHideOnMobile : ''} ${className}`}>
      <SaveWatcher className={styles.save} />
      <button className={styles.button} title="Undo" onClick={() => saveService.undo()} disabled={!undoStack.length}>
        <Icon name="undo" />
      </button>
      <button className={styles.button} title="Redo" onClick={() => saveService.redo()} disabled={!redoStack.length}>
        <Icon name="redo" />
      </button>

      {!liteControls && (
        <>
          {options.map(option => {
            if (option.type === 'spacer') return null
            if (!option.id || option.hidden) return null
            if (!viewableDeckControls[option.id]) return null

            const button = (
              // @ts-ignore
              <button className={styles.button} title={option.label || ''} onClick={option.onClick} key={option.id}>
                {option.icon && <Icon name={option.icon} />}
                {option.customIcon && <CustomIcon name={option.customIcon} />}
              </button>
            )

            if (option.to) {
              return (
                <Link href={option.to} key={option.id}>
                  {button}
                </Link>
              )
            }

            return button
          })}

          <ContextMenu
            openOnLeftClick
            pinToLeftFirst
            menuClassName={styles.extrasMenu}
            options={[
              ...options,
              { type: 'spacer' },
              {
                label: 'Add / show options',
                type: 'extras',
                id: 'stickied-buttons',
                icon: 'eye slash',
                options: [
                  {
                    label: 'Select all',
                    onClick: () => handleSetAllLocalViewableToggles(true),
                    noCloseOnClick: true,
                    icon: 'eye',
                  },
                  {
                    label: 'Select none',
                    onClick: () => handleSetAllLocalViewableToggles(false),
                    noCloseOnClick: true,
                    icon: 'eye slash',
                  },
                  { type: 'spacer' },
                  ...options.reduce((acc, option) => {
                    if (option.type === 'spacer') return acc
                    if (!option.id || option.hidden) return acc

                    acc.push({
                      type: 'checkbox',
                      // @ts-ignore
                      label: option.label || '',
                      // @ts-ignore
                      onChange: () => handleToggleViewableDeckControls(option.id),
                      checked: localViewableContorls[option.id],
                    })

                    return acc
                  }, [] as Option[]),

                  { type: 'spacer' },
                  { label: 'Apply view settings', onClick: handleApplyControlChanges, icon: 'save outline' },
                ],
              },
            ]}>
            <button className={styles.button} title="Extras">
              <Icon name="ellipsis horizontal" />
            </button>
          </ContextMenu>
        </>
      )}
    </div>
  )
}

export default StickiedDeckControls
